// src/pages/shortStory/ShortStoryGeneratingPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthHeaders, urlMap } from '../../utils';
import TopBar from '../components/TopBar';
import '../../assets/styles/components/ShortStory.css';

export default function ShortStoryGeneratingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const [phase, setPhase] = useState('starting'); // starting | streaming | done | error
  const [bubbles, setBubbles] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  }, []);

  const addBubble = useCallback(
    (text, type = 'log') => {
      setBubbles((prev) => [...prev, { id: `bubble-${Date.now()}-${Math.random()}`, text, type }]);
      scrollToBottom();
    },
    [scrollToBottom],
  );

  const handleDone = useCallback(
    (finishedProjectId) => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setProjectId(finishedProjectId);
      setPhase('done');
      addBubble('Your draft is ready! Click below to start writing.', 'done');
    },
    [addBubble],
  );

  const openStream = useCallback(
    async (generationId) => {
      abortRef.current = new AbortController();
      try {
        const res = await fetch(urlMap.shortStoryStream(generationId), {
          method: 'GET',
          headers: { ...getAuthHeaders(), Accept: 'text/event-stream' },
          credentials: 'include',
          signal: abortRef.current.signal,
        });

        if (!res.ok || !res.body) {
          setPhase('error');
          setErrorMessage('Could not connect to the generation stream.');
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finished = false;

        const feed = (chunk) => {
          buffer += chunk;
          const frames = buffer.split('\n\n');
          buffer = frames.pop() || '';

          for (const frame of frames) {
            for (const line of frame.split('\n')) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              try {
                const evt = JSON.parse(trimmed.slice(5).trim());
                if (evt.type === 'connected') continue;
                if (evt.type === 'log') {
                  addBubble(evt.message, 'log');
                } else if (evt.type === 'done') {
                  handleDone(evt.projectId);
                  finished = true;
                } else if (evt.type === 'error') {
                  addBubble(evt.message, 'error');
                  setPhase('error');
                  setErrorMessage(evt.message);
                  finished = true;
                }
              } catch {
                // Ignore malformed frames
              }
            }
          }
        };

        while (!finished) {
          const { value, done } = await reader.read();
          if (done) break;
          feed(decoder.decode(value, { stream: true }));
        }
        if (!finished) feed(decoder.decode());

        if (!finished) {
          setPhase((prev) => (prev === 'streaming' ? 'error' : prev));
          setErrorMessage((prev) => prev ?? 'Connection closed unexpectedly.');
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          setPhase('error');
          setErrorMessage('Connection lost. Please try again.');
        }
      }
    },
    [addBubble, handleDone],
  );

  const startGeneration = useCallback(async () => {
    if (!sessionId) {
      setPhase('error');
      setErrorMessage('Missing session ID. Go back and try again.');
      return;
    }

    setPhase('starting');
    setBubbles([]);
    setErrorMessage(null);
    setProjectId(null);

    try {
      const res = await fetch(urlMap.shortStoryGenerate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ chatSessionId: sessionId }),
      });
      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw Object.assign(new Error(payload?.error || `HTTP_${res.status}`), { body: payload });

      if (payload.alreadyDone) {
        handleDone(payload.projectId);
        return;
      }

      setPhase('streaming');
      openStream(payload.generationId);
    } catch (err) {
      setPhase('error');
      setErrorMessage(err?.body?.error || err?.message || 'Could not start generation. Please try again.');
    }
  }, [sessionId, openStream, handleDone]);

  useEffect(() => {
    startGeneration();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleViewDraft = () => {
    navigate(`/project/${projectId}/library`);
  };

  return (
    <div className="home-page short-story-page">
      <TopBar />
      <main className="home-main short-story-main">
        <div className="short-story-panel">
          <div className="short-story-panel__header">
            <h2 className="short-story-panel__title">
              Writing your draft…
              {phase === 'streaming' && <span className="short-story-spinner inline" />}
            </h2>
          </div>

          <div className="short-story-log" ref={scrollRef}>
            {phase === 'starting' && (
              <div className="short-story-centered">
                <div className="short-story-spinner" />
                <p className="short-story-muted">Starting up…</p>
              </div>
            )}

            {bubbles.map((b) => (
              <div key={b.id} className="short-story-bubble-row left">
                <div
                  className={`short-story-bubble log ${b.type === 'error' ? 'error' : ''} ${b.type === 'done' ? 'done' : ''}`}
                >
                  {b.text}
                </div>
              </div>
            ))}
          </div>

          {phase === 'done' && (
            <div className="short-story-action-area">
              <button type="button" className="short-story-btn primary full" onClick={handleViewDraft}>
                View my draft →
              </button>
            </div>
          )}

          {phase === 'error' && (
            <div className="short-story-action-area">
              {errorMessage && bubbles.length === 0 && <p className="short-story-error">{errorMessage}</p>}
              <button type="button" className="short-story-btn primary full" onClick={startGeneration}>
                Try again
              </button>
              <button type="button" className="short-story-btn link" onClick={() => navigate(-1)}>
                Go back
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
